/**
 * Copy of the bill-side Cost Services screens (ALC-4476): the destination of a bill line, the
 * provision picker and, once checkpoint B lands, the readiness consent, the undo and the header
 * badge. Spread into the `bills` namespace as the top-level `costMatches` block.
 *
 * Error sentences are NOT here: they live once in `COST_SERVICE_ERROR_MESSAGES` (`@alinea/types`)
 * and are rendered from the stable `code`. The reasons the candidate engine returns are stable
 * codes too, so their sentences are here, keyed by the code.
 */
export const costMatchesPtBr = {
  section: {
    title: 'Baixa de provisão',
    description: 'Diga se cada linha é despesa ou paga uma provisão de custo já reconhecida.',
    lineLabel: 'Linha {{number}}',
    lineDescriptionFallback: 'Sem descrição',
    noLines: 'Esta conta ainda não tem linhas.',
    readOnlyNote:
      'O destino das linhas está fixado: ele só muda enquanto a conta está em rascunho ou foi devolvida para ajuste.',
  },
  // D44g: the link under "Custo de serviço" and its dialog (was the fixed "Baixa de provisão" band).
  reliefLink: {
    settled: 'Provisão baixada',
    awaiting: 'Antecipação aguardando provisão',
    undone: 'Baixa desfeita',
    lineLabel: 'Linha {{number}}: {{label}}',
    dialogTitle: 'Baixa de provisão',
    dialogDescription: 'As provisões de custo que cada linha baixou ou aguarda, e como desfazer.',
  },
  destination: {
    label: 'Destino da linha',
    options: {
      EXPENSE: 'Despesa',
      COST_PROVISION: 'Serviço de custo',
    },
    saving: 'Salvando o destino…',
    sourceLabel: 'Definido por',
    source: {
      USER: 'Usuário',
      INTAKE_RULE: 'Regra de intake',
      VENDOR_PRECEDENT: 'Inferido da fatura anterior',
      COST_SERVICE_FLAG: 'Marcado automaticamente (serviço de custo)',
      MCP: 'MCP',
      UNDO: 'Desfeito',
      UNKNOWN: 'Não informado',
    },
    flagSourceNamed: 'Marcado automaticamente (serviço de custo «{{name}}»)',
    undoHint: 'A baixa desta linha foi desfeita e ela voltou a ser despesa.',
    switchToExpense: {
      title: 'Mudar a linha {{number}} para despesa?',
      description_one:
        'A provisão escolhida para esta linha será descartada. Nenhuma baixa foi feita ainda, então nada é estornado.',
      description_other:
        'As {{count}} provisões escolhidas para esta linha serão descartadas. Nenhuma baixa foi feita ainda, então nada é estornado.',
      descriptionUnknown:
        'As provisões escolhidas para esta linha, se houver, serão descartadas. Nenhuma baixa foi feita ainda, então nada é estornado.',
      confirm: 'Mudar para despesa',
      success: 'A linha {{number}} agora é despesa.',
    },
  },
  picker: {
    expected: {
      tag: 'esperada',
      qualifier:
        '(esperada: a provisão desta competência ainda não foi reconhecida; a parte fica no antecipado até ela existir)',
      inSet: 'Inclui provisão esperada',
    },
    title: 'Provisões que esta linha paga',
    loading: 'Buscando as provisões em aberto do fornecedor…',
    loadError: 'Não foi possível buscar as provisões desta linha.',
    retry: 'Tentar de novo',
    refresh: 'Atualizar sugestões',
    summary: {
      lineAmount: 'Valor da linha',
      alreadyApplied: 'Já baixado',
      remaining: 'A baixar',
      selected: 'Selecionado',
      difference: 'Diferença',
    },
    matchClass: {
      PRONTA: 'Pronta',
      AMBIGUA: 'Ambígua',
      SEM_CANDIDATA: 'Sem candidata',
    },
    matchClassLabel: 'Situação',
    reason: {
      SINGLE_SET_WITHIN_TOLERANCE:
        'Um único conjunto de provisões fecha o valor da linha dentro da tolerância.',
      MULTIPLE_SETS_WITHIN_TOLERANCE:
        'Mais de um conjunto de provisões fecha o valor da linha dentro da tolerância. Escolha qual vale.',
      NO_SET_WITHIN_TOLERANCE:
        'Há provisões em aberto, mas nenhuma combinação fecha o valor da linha dentro da tolerância. Divida o valor à mão.',
      NO_OPEN_ENTRIES: 'Este fornecedor não tem provisões em aberto para esta conta.',
      OPEN_ENTRIES_IN_OTHER_CURRENCY:
        'As provisões em aberto deste fornecedor estão em outra moeda que a da conta.',
      BEST_SCORE_BELOW_FLOOR:
        'As provisões em aberto combinam pouco com esta linha. Confira antes de escolher.',
      LINE_AMOUNT_NOT_POSITIVE: 'A linha não tem valor positivo, então não há o que baixar.',
      SINGLE_CROSS_SERVICE_SET_WITHIN_TOLERANCE:
        'Nenhum serviço de custo sozinho fecha a linha, mas uma única combinação entre os serviços do fornecedor fecha dentro da tolerância.',
      MULTIPLE_CROSS_SERVICE_SETS_WITHIN_TOLERANCE:
        'Mais de uma combinação entre os serviços de custo do fornecedor fecha o valor da linha. Escolha qual vale ou divida por serviço.',
      UNKNOWN: 'Confira as provisões abaixo antes de escolher.',
    },
    otherCurrency_one:
      'O fornecedor tem {{count}} provisão em aberto em outra moeda, que não aparece aqui.',
    otherCurrency_other:
      'O fornecedor tem {{count}} provisões em aberto em outra moeda, que não aparecem aqui.',
    sets: {
      title: 'Conjuntos sugeridos',
      kind: {
        SINGLE_ENTRY: 'Uma provisão',
        MONTH_RUN: 'Meses seguidos',
        CROSS_SERVICE: 'Combinação entre serviços',
      },
      part: '{{service}}: {{amount}}',
      entries_one: '{{count}} provisão',
      entries_other: '{{count}} provisões',
      range: '{{first}} a {{last}}',
      score: 'Pontuação {{score}} de 100',
      openTotal: 'Em aberto: {{amount}}',
      difference: 'Diferença para a linha: {{amount}}',
      accept: 'Aceitar este conjunto',
      accepted: 'Conjunto escolhido',
      empty:
        'Nenhum conjunto fecha o valor da linha. Mantenha a baixa automática ou confira o valor da linha.',
    },
    serviceChoice: {
      label: 'Serviço de custo',
      description:
        'Este fornecedor tem mais de um serviço de custo. Escolha qual esta linha paga, ou divida o valor entre eles.',
      all: 'Todos',
      split: 'Dividir',
      saving: 'Salvando a escolha…',
      splitTitle: 'Valor por serviço de custo',
      amountLabel: 'Valor de {{service}}',
      total: 'Total dividido',
      remaining: 'Falta dividir',
      sumMismatch: 'A soma das partes precisa ser igual ao valor da linha ({{amount}}).',
      invalidAmount: 'Informe um valor positivo, como 1000,00.',
      save: 'Salvar divisão',
      saved: 'Serviço de custo da linha salvo.',
      partStatus: 'Parte {{amount}} · selecionado {{selected}}',
      confirmDialog: {
        title: 'Mudar o serviço de custo da linha {{number}}?',
        description:
          'As provisões já escolhidas que não couberem na nova escolha serão descartadas. Nenhuma baixa foi feita ainda, então nada é estornado.',
        confirm: 'Mudar serviço de custo',
        success: 'O serviço de custo da linha {{number}} foi atualizado.',
      },
    },
    anticipate: {
      title: 'Antecipar mês',
      description:
        'A provisão do mês ainda não existe? Reserve parte desta linha para ela. A parte fica no antecipado até a provisão ser lançada.',
      service: 'Serviço de custo',
      servicePlaceholder: 'Escolha o serviço',
      month: 'Competência',
      amount: 'Valor',
      submit: 'Antecipar mês',
      saved: 'Mês antecipado. A parte aparece na seleção atual como esperada.',
      invalidService: 'Escolha o serviço de custo.',
      invalidMonth: 'Informe a competência.',
      invalidAmount: 'Informe um valor positivo, como 1000,00.',
      overLine: 'Com este valor, a seleção passa o valor da linha.',
    },
    current: {
      title: 'Seleção atual',
      empty: 'Nenhuma provisão escolhida ainda.',
      rowLabel: '{{service}} · {{month}}',
      source: {
        SUGGESTED: 'Sugestão aceita',
        AUTO: 'Preenchida automaticamente',
        AUTO_FIFO: 'Baixa automática',
        MANUAL: 'Divisão manual',
        MCP: 'Via MCP',
      },
      autoNote:
        'Esta seleção foi preenchida automaticamente. Confira antes de enviar a conta para aprovação.',
      total: 'Total selecionado: {{amount}}',
      clear: 'Limpar seleção',
      clearDialog: {
        title: 'Limpar a seleção da linha {{number}}?',
        description_one:
          'A provisão escolhida será descartada. Nenhuma baixa foi feita ainda, então nada é estornado.',
        description_other:
          'As {{count}} provisões escolhidas serão descartadas. Nenhuma baixa foi feita ainda, então nada é estornado.',
        confirm: 'Limpar seleção',
        success: 'A seleção da linha {{number}} foi limpa.',
      },
    },
    issue: {
      pending: 'Escolha as provisões que esta linha paga antes de enviar a conta para aprovação.',
      varianceTitle: 'Diferença acima da tolerância',
      varianceNote: 'Você poderá seguir ao enviar a conta para aprovação, confirmando a variação.',
      blockingTitle: 'A seleção precisa de ajuste',
    },
  },
  automatic: {
    title: 'Baixa automática',
    summary: 'Baixa automática: {{amount}} de {{service}} (provisões mais antigas primeiro)',
    oneMonth: '{{month}}: {{applied}} de {{available}}',
    months: '{{first}} a {{last}} · {{count}} provisões',
    remaining: 'restam {{amount}} em aberto',
    prepaid: '{{amount}} fica no antecipado até a provisão de {{month}}',
    expense: '{{amount}} vai como despesa em {{account}}',
    variance: 'Diferença lançada como variação · {{amount}}',
    nothing: 'Nada a baixar: a linha já foi baixada.',
    service: 'Serviço de custo',
    showProvisions: 'Ver provisões',
    allocationRow: '{{month}} · {{applied}} de {{available}}',
    allocationRemaining: 'restam {{amount}}',
    adjust: 'Ajustar',
    closeAdjust: 'Fechar ajuste',
    previewNote: 'Prévia: a aprovação recalcula com os saldos do momento.',
    manualTitle: 'Baixa ajustada à mão',
    manualNote: 'A aprovação usa as provisões escolhidas abaixo, não a baixa automática.',
    backToAutomatic: 'Voltar ao automático',
    backToAutomaticDone: 'A linha {{number}} voltou para a baixa automática.',
    serviceSaved: 'Serviço de custo da linha salvo.',
    prefillTooltip:
      'Preenchido automaticamente: a combinação de provisões em aberto que fecha o valor da linha, ou o serviço marcado para este fornecedor.',
    backToPrefill: 'Voltar ao automático',
    backToPrefillDone: 'A linha {{number}} voltou para a divisão automática.',
  },
  serviceSelector: {
    placeholder: 'Selecione o serviço de custo...',
    search: 'Buscar serviço de custo...',
    empty: 'Nenhum serviço de custo encontrado.',
    clear: 'Limpar seleção',
    open: '{{amount}} em aberto',
    loadError: 'Erro ao carregar os serviços de custo.',
    remove: 'Remover {{name}}',
  },
  link: {
    label: 'Vincular a custo de serviço',
    noVendorServices:
      'Nenhum custo de serviço ativo de {{vendor}} na data desta conta. Para vincular, defina este fornecedor em um custo de serviço.',
    noVendorServicesNoName:
      'Nenhum custo de serviço ativo deste fornecedor na data desta conta. Para vincular, defina o fornecedor em um custo de serviço.',
    openCostServices: 'Abrir Serviços de custo',
    splitLabel: 'Rateio entre serviços de custo',
    scopeAllTooltip: 'O serviço escolhido vale para todas as linhas desta conta.',
    lineLabel: 'Linha {{number}}',
    lineExpense: 'Despesa',
    previewInfo: 'Prévia: a aprovação recalcula com os saldos do momento.',
    detailOpen: 'Ver a baixa automática',
    reliefDetail: 'Baixa automática: {{amount}} de {{service}}',
    lineError: 'Linha {{number}}: {{message}}',
    clearConfirm: {
      title: 'Desvincular a conta do custo de serviço?',
      description:
        'As provisões escolhidas para as linhas serão descartadas e a conta volta a ser despesa.',
      confirm: 'Desvincular',
      done: 'A conta voltou a ser despesa.',
    },
  },
  consent: {
    intro:
      'A diferença entre a linha e a provisão passa da tolerância do serviço de custo. A conta pode seguir: a diferença será lançada na conta de variação e o seu aceite fica no histórico da conta.',
    line: 'Linha {{number}}: diferença de {{amount}}, acima da tolerância de {{tolerance}}.',
    toleranceExact: 'exata',
    checkbox: 'Confirmo a diferença acima da tolerância e quero enviar a conta mesmo assim.',
    footerNote: 'Marque a confirmação para enviar a conta.',
    bulkCheckbox_one:
      'Confirmo a diferença acima da tolerância de {{count}} conta e quero enviá-la mesmo assim.',
    bulkCheckbox_other:
      'Confirmo a diferença acima da tolerância de {{count}} contas e quero enviá-las mesmo assim.',
    bulkFooterNote: 'Marque a confirmação para enviar as contas com diferença.',
    bulkBills_one: '{{count}} conta com diferença acima da tolerância',
    bulkBills_other: '{{count}} contas com diferença acima da tolerância',
  },
  openProvisions: {
    sentence:
      'Este fornecedor tem {{amount}} em provisões abertas de {{services}}. Aprovar como despesa pode contar o custo duas vezes.',
    sentenceNoService:
      'Este fornecedor tem {{amount}} em provisões abertas. Aprovar como despesa pode contar o custo duas vezes.',
    link: 'Ver provisões em aberto',
    title: 'Aprovar mesmo assim?',
    description:
      'A conta pode ser aprovada. Ao confirmar, seu aceite fica registrado no histórico da conta.',
    confirm: 'Aprovar mesmo assim',
  },
  badge: {
    label: 'Baixa de provisão: {{applied}} de {{total}}',
  },
  settled: {
    title: 'Provisões baixadas por esta linha',
    row: '{{service}} · {{month}}',
    unknownService: 'Provisão',
    variance: 'Diferença: {{amount}}',
    total: 'Total baixado da linha',
    openProvision: 'Ver provisão',
    undo: 'Desfazer baixa',
    reclassOn: 'Baixada em {{date}}',
  },
  undone: {
    title: 'Baixas desfeitas',
    when: 'Desfeita em {{date}}',
    reason: 'Motivo: {{reason}}',
    noReason: 'Sem motivo registrado',
  },
  undo: {
    title: 'Desfazer a baixa da linha {{number}}?',
    description:
      'A baixa é estornada com um lançamento de hoje: as provisões abaixo voltam a ficar em aberto e a linha volta a ser despesa.',
    reasonLabel: 'Motivo',
    reasonPlaceholder: 'Por que a baixa está sendo desfeita?',
    footerNote: 'Informe o motivo para desfazer a baixa.',
    confirm: 'Desfazer baixa',
    success: 'A baixa da linha {{number}} foi desfeita. As provisões voltaram a ficar em aberto.',
  },
  awaiting: {
    title: 'Aguardando provisão (antecipado)',
    titleWithMonth: 'Aguardando provisão (antecipado) · {{month}} · {{amount}}',
    consumed: 'Baixada em {{date}}',
    consumedPartial: 'Baixada em {{date}}: {{consumed}} de {{amount}}',
    leftover: 'Saldo ainda antecipado: {{amount}}',
    released: '{{status}} em {{date}} · Motivo: {{reason}}',
    status: {
      CANCELLED: 'Desfeita',
      CLOSED: 'Encerrada',
      VOIDED: 'Anulada com a conta',
    },
    sectionTitle: 'Partes antecipadas aguardando provisão',
    undo: 'Desfazer antecipação',
    close: 'Encerrar antecipação',
    undoDialog: {
      title: 'Desfazer a antecipação da linha {{number}}?',
      description:
        'A parte antecipada volta a ser despesa com um lançamento de hoje (débito em despesa, crédito no antecipado). As provisões esperadas deixam de aguardar esta linha.',
      reasonLabel: 'Motivo',
      reasonPlaceholder: 'Por que a antecipação está sendo desfeita?',
      footerNote: 'Informe o motivo para desfazer a antecipação.',
      confirm: 'Desfazer antecipação',
      success: 'A antecipação da linha {{number}} foi desfeita. A parte voltou a ser despesa.',
    },
    body: 'Esta parte paga a competência {{month}}, que ainda não foi provisionada. Ela será baixada quando a provisão existir.',
    bodyWithAmount:
      'Esta parte, de {{amount}}, paga a competência {{month}}, que ainda não foi provisionada. Ela será baixada quando a provisão existir.',
  },
} as const;

export const costMatchesEn = {
  section: {
    title: 'Provision settlement',
    description: 'Say whether each line is an expense or pays a cost provision already recognized.',
    lineLabel: 'Line {{number}}',
    lineDescriptionFallback: 'No description',
    noLines: 'This bill has no lines yet.',
    readOnlyNote:
      'The line destinations are fixed: they only change while the bill is a draft or was sent back for changes.',
  },
  reliefLink: {
    settled: 'Provision relieved',
    awaiting: 'Prepaid, waiting for the provision',
    undone: 'Relief undone',
    lineLabel: 'Line {{number}}: {{label}}',
    dialogTitle: 'Provision settlement',
    dialogDescription: 'The cost provisions each line relieved or awaits, and how to undo it.',
  },
  destination: {
    label: 'Line destination',
    options: {
      EXPENSE: 'Expense',
      COST_PROVISION: 'Cost service',
    },
    saving: 'Saving the destination…',
    sourceLabel: 'Set by',
    source: {
      USER: 'User',
      INTAKE_RULE: 'Intake rule',
      VENDOR_PRECEDENT: 'Inferred from the previous bill',
      COST_SERVICE_FLAG: 'Marked automatically (cost service)',
      MCP: 'MCP',
      UNDO: 'Undone',
      UNKNOWN: 'Not recorded',
    },
    flagSourceNamed: 'Marked automatically (cost service “{{name}}”)',
    undoHint: 'The match of this line was undone and it went back to being an expense.',
    switchToExpense: {
      title: 'Change line {{number}} to expense?',
      description_one:
        'The provision chosen for this line will be discarded. Nothing was settled yet, so nothing is reversed.',
      description_other:
        'The {{count}} provisions chosen for this line will be discarded. Nothing was settled yet, so nothing is reversed.',
      descriptionUnknown:
        'The provisions chosen for this line, if any, will be discarded. Nothing was settled yet, so nothing is reversed.',
      confirm: 'Change to expense',
      success: 'Line {{number}} is now an expense.',
    },
  },
  picker: {
    expected: {
      tag: 'expected',
      qualifier:
        "(expected: this competency's provision is not recognised yet; the part stays prepaid until it exists)",
      inSet: 'Includes an expected provision',
    },
    title: 'Provisions this line pays',
    loading: 'Looking up the vendor open provisions…',
    loadError: 'The provisions for this line could not be loaded.',
    retry: 'Try again',
    refresh: 'Refresh suggestions',
    summary: {
      lineAmount: 'Line amount',
      alreadyApplied: 'Already settled',
      remaining: 'To settle',
      selected: 'Selected',
      difference: 'Difference',
    },
    matchClass: {
      PRONTA: 'Ready',
      AMBIGUA: 'Ambiguous',
      SEM_CANDIDATA: 'No candidate',
    },
    matchClassLabel: 'Status',
    reason: {
      SINGLE_SET_WITHIN_TOLERANCE:
        'A single set of provisions covers the line amount within the tolerance.',
      MULTIPLE_SETS_WITHIN_TOLERANCE:
        'More than one set of provisions covers the line amount within the tolerance. Choose which one applies.',
      NO_SET_WITHIN_TOLERANCE:
        'There are open provisions, but no combination covers the line amount within the tolerance. Split the amount by hand.',
      NO_OPEN_ENTRIES: 'This vendor has no open provisions for this bill.',
      OPEN_ENTRIES_IN_OTHER_CURRENCY:
        'The open provisions of this vendor are in another currency than the bill.',
      BEST_SCORE_BELOW_FLOOR:
        'The open provisions are a weak match for this line. Check them before choosing.',
      LINE_AMOUNT_NOT_POSITIVE: 'The line has no positive amount, so there is nothing to settle.',
      SINGLE_CROSS_SERVICE_SET_WITHIN_TOLERANCE:
        "No single cost service closes the line, but exactly one combination across the vendor's services closes it within tolerance.",
      MULTIPLE_CROSS_SERVICE_SETS_WITHIN_TOLERANCE:
        "More than one combination across the vendor's cost services closes the line amount. Choose which one applies or split by service.",
      UNKNOWN: 'Check the provisions below before choosing.',
    },
    otherCurrency_one:
      'The vendor has {{count}} open provision in another currency, not shown here.',
    otherCurrency_other:
      'The vendor has {{count}} open provisions in another currency, not shown here.',
    sets: {
      title: 'Suggested sets',
      kind: {
        SINGLE_ENTRY: 'One provision',
        MONTH_RUN: 'Consecutive months',
        CROSS_SERVICE: 'Across services',
      },
      part: '{{service}}: {{amount}}',
      entries_one: '{{count}} provision',
      entries_other: '{{count}} provisions',
      range: '{{first}} to {{last}}',
      score: 'Score {{score}} of 100',
      openTotal: 'Open: {{amount}}',
      difference: 'Difference from the line: {{amount}}',
      accept: 'Accept this set',
      accepted: 'Chosen set',
      empty: 'No set covers the line amount. Keep the automatic relief or check the line amount.',
    },
    serviceChoice: {
      label: 'Cost service',
      description:
        'This vendor has more than one cost service. Choose which one this line pays, or split the amount between them.',
      all: 'All',
      split: 'Split',
      saving: 'Saving the choice…',
      splitTitle: 'Amount per cost service',
      amountLabel: 'Amount for {{service}}',
      total: 'Total split',
      remaining: 'Left to split',
      sumMismatch: 'The parts must add up to the line amount ({{amount}}).',
      invalidAmount: 'Enter a positive amount, such as 1000.00.',
      save: 'Save split',
      saved: 'Cost service of the line saved.',
      partStatus: 'Part {{amount}} · selected {{selected}}',
      confirmDialog: {
        title: 'Change the cost service of line {{number}}?',
        description:
          'Provisions already chosen that do not fit the new choice will be discarded. Nothing was settled yet, so nothing is reversed.',
        confirm: 'Change cost service',
        success: 'The cost service of line {{number}} was updated.',
      },
    },
    anticipate: {
      title: 'Bring a month forward',
      description:
        "The month's provision does not exist yet? Reserve part of this line for it. The part stays prepaid until the provision posts.",
      service: 'Cost service',
      servicePlaceholder: 'Choose the service',
      month: 'Competency',
      amount: 'Amount',
      submit: 'Bring month forward',
      saved: 'Month brought forward. The part shows in the current selection as expected.',
      invalidService: 'Choose the cost service.',
      invalidMonth: 'Enter the competency month.',
      invalidAmount: 'Enter a positive amount, such as 1000.00.',
      overLine: 'With this amount the selection goes over the line amount.',
    },
    current: {
      title: 'Current selection',
      empty: 'No provision chosen yet.',
      rowLabel: '{{service}} · {{month}}',
      source: {
        SUGGESTED: 'Accepted suggestion',
        AUTO: 'Filled in automatically',
        AUTO_FIFO: 'Automatic relief',
        MANUAL: 'Manual split',
        MCP: 'Via MCP',
      },
      autoNote:
        'This selection was filled in automatically. Check it before sending the bill for approval.',
      total: 'Total selected: {{amount}}',
      clear: 'Clear selection',
      clearDialog: {
        title: 'Clear the selection of line {{number}}?',
        description_one:
          'The chosen provision will be discarded. Nothing was settled yet, so nothing is reversed.',
        description_other:
          'The {{count}} chosen provisions will be discarded. Nothing was settled yet, so nothing is reversed.',
        confirm: 'Clear selection',
        success: 'The selection of line {{number}} was cleared.',
      },
    },
    issue: {
      pending: 'Choose the provisions this line pays before sending the bill for approval.',
      varianceTitle: 'Difference above the tolerance',
      varianceNote:
        'You can still move on when sending the bill for approval, by confirming the variance.',
      blockingTitle: 'The selection needs a change',
    },
  },
  automatic: {
    title: 'Automatic relief',
    summary: 'Automatic relief: {{amount}} of {{service}} (oldest provisions first)',
    oneMonth: '{{month}}: {{applied}} of {{available}}',
    months: '{{first}} to {{last}} · {{count}} provisions',
    remaining: '{{amount}} stays open',
    prepaid: '{{amount}} waits as prepaid for the {{month}} provision',
    expense: '{{amount}} posts as an expense to {{account}}',
    variance: 'Difference posted as variance · {{amount}}',
    nothing: 'Nothing to relieve: the line was already relieved.',
    service: 'Cost service',
    showProvisions: 'Show provisions',
    allocationRow: '{{month}} · {{applied}} of {{available}}',
    allocationRemaining: '{{amount}} left',
    adjust: 'Adjust',
    closeAdjust: 'Close adjustment',
    previewNote: 'Preview: approval recomputes it with the balances at that moment.',
    manualTitle: 'Relief adjusted by hand',
    manualNote: 'Approval uses the provisions chosen below, not the automatic relief.',
    backToAutomatic: 'Back to automatic',
    backToAutomaticDone: 'Line {{number}} is back on the automatic relief.',
    serviceSaved: 'Cost service of the line saved.',
    prefillTooltip:
      'Filled in automatically: the combination of open provisions that adds up to the line, or the service flagged for this vendor.',
    backToPrefill: 'Back to automatic',
    backToPrefillDone: 'Line {{number}} is back on the automatic split.',
  },
  serviceSelector: {
    placeholder: 'Select the cost service...',
    search: 'Search cost services...',
    empty: 'No cost service found.',
    clear: 'Clear selection',
    open: '{{amount}} open',
    loadError: 'Could not load the cost services.',
    remove: 'Remove {{name}}',
  },
  link: {
    label: 'Link to a cost service',
    noVendorServices:
      'No active cost service of {{vendor}} on this bill date. To link it, set this vendor on a cost service.',
    noVendorServicesNoName:
      'No active cost service of this vendor on this bill date. To link it, set the vendor on a cost service.',
    openCostServices: 'Open Cost services',
    splitLabel: 'Split across cost services',
    scopeAllTooltip: 'The chosen service applies to every line of this bill.',
    lineLabel: 'Line {{number}}',
    lineExpense: 'Expense',
    previewInfo: 'Preview: approval recomputes with the balances at that time.',
    detailOpen: 'Show the automatic relief',
    reliefDetail: 'Automatic relief: {{amount}} of {{service}}',
    lineError: 'Line {{number}}: {{message}}',
    clearConfirm: {
      title: 'Unlink the bill from the cost service?',
      description:
        'The provisions chosen for the lines will be dropped and the bill becomes an expense again.',
      confirm: 'Unlink',
      done: 'The bill is an expense again.',
    },
  },
  consent: {
    intro:
      'The difference between the line and the provision is above the Cost Service tolerance. The bill can go on: the difference is posted to the variance account and your confirmation is recorded in the bill history.',
    line: 'Line {{number}}: difference of {{amount}}, above the tolerance of {{tolerance}}.',
    toleranceExact: 'exact',
    checkbox: 'I confirm the difference above the tolerance and want to send the bill anyway.',
    footerNote: 'Tick the confirmation to send the bill.',
    bulkCheckbox_one:
      'I confirm the difference above the tolerance of {{count}} bill and want to send it anyway.',
    bulkCheckbox_other:
      'I confirm the difference above the tolerance of {{count}} bills and want to send them anyway.',
    bulkFooterNote: 'Tick the confirmation to send the bills with a difference.',
    bulkBills_one: '{{count}} bill with a difference above the tolerance',
    bulkBills_other: '{{count}} bills with a difference above the tolerance',
  },
  openProvisions: {
    sentence:
      'This vendor has {{amount}} in open {{services}} provisions. Approving as an expense may count the cost twice.',
    sentenceNoService:
      'This vendor has {{amount}} in open provisions. Approving as an expense may count the cost twice.',
    link: 'See open provisions',
    title: 'Approve anyway?',
    description:
      'The bill can still be approved. When you confirm, your acknowledgement is recorded in the bill history.',
    confirm: 'Approve anyway',
  },
  badge: {
    label: 'Provision settlement: {{applied}} of {{total}}',
  },
  settled: {
    title: 'Provisions settled by this line',
    row: '{{service}} · {{month}}',
    unknownService: 'Provision',
    variance: 'Difference: {{amount}}',
    total: 'Total settled by the line',
    openProvision: 'View provision',
    undo: 'Undo settlement',
    reclassOn: 'Settled on {{date}}',
  },
  undone: {
    title: 'Settlements undone',
    when: 'Undone on {{date}}',
    reason: 'Reason: {{reason}}',
    noReason: 'No reason recorded',
  },
  undo: {
    title: 'Undo the settlement of line {{number}}?',
    description:
      'The settlement is reversed with an entry dated today: the provisions below become open again and the line goes back to being an expense.',
    reasonLabel: 'Reason',
    reasonPlaceholder: 'Why is the settlement being undone?',
    footerNote: 'Enter the reason to undo the settlement.',
    confirm: 'Undo settlement',
    success: 'The settlement of line {{number}} was undone. The provisions are open again.',
  },
  awaiting: {
    title: 'Awaiting provision (prepaid)',
    titleWithMonth: 'Awaiting provision (prepaid) · {{month}} · {{amount}}',
    consumed: 'Settled on {{date}}',
    consumedPartial: 'Settled on {{date}}: {{consumed}} of {{amount}}',
    leftover: 'Still prepaid: {{amount}}',
    released: '{{status}} on {{date}} · Reason: {{reason}}',
    status: {
      CANCELLED: 'Undone',
      CLOSED: 'Closed',
      VOIDED: 'Voided with the bill',
    },
    sectionTitle: 'Prepaid parts awaiting their provision',
    undo: 'Undo prepayment',
    close: 'Close prepayment',
    undoDialog: {
      title: 'Undo the prepayment of line {{number}}?',
      description:
        'The prepaid part goes back to expense with an entry dated today (debit expense, credit prepaid). The expected provisions stop waiting for this line.',
      reasonLabel: 'Reason',
      reasonPlaceholder: 'Why is the prepayment being undone?',
      footerNote: 'Enter the reason to undo the prepayment.',
      confirm: 'Undo prepayment',
      success: 'The prepayment of line {{number}} was undone. The part is an expense again.',
    },
    body: 'This part pays the {{month}} competency, which has not been provisioned yet. It will be settled when the provision exists.',
    bodyWithAmount:
      'This part, of {{amount}}, pays the {{month}} competency, which has not been provisioned yet. It will be settled when the provision exists.',
  },
} as const;
